// src/components/MyLibraryScreen.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  مكتبتي — مع تخزين الكتب offline مشفّرة
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import offlineStorage from '../services/offlineStorage';
import { getBackblazeDownloadUrl, getMediaUrl } from '../config/backblaze';
import { motion } from 'framer-motion';
import {
  FiArrowRight, FiBook, FiFolder, FiLink,
  FiDownload, FiExternalLink, FiRefreshCw,
  FiWifiOff, FiPlay, FiCheckCircle, FiClock,
  FiHardDrive,
} from 'react-icons/fi';

// ─────────────────────────────────────────────────────
//  دالة تحميل الملف مع Watermark (PDF فقط)
// ─────────────────────────────────────────────────────
async function addWatermarkToPdfBytes(pdfBlob, userInfo) {
  try {
    const { PDFDocument, rgb, degrees, StandardFonts } = await import('pdf-lib');
    const arrayBuffer = await pdfBlob.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const pages = pdfDoc.getPages();
    const email   = userInfo?.email || '';
    const userId  = userInfo?.id    || '';
    const shortId = userId.slice(0, 8).toUpperCase();
    const mainText   = email || shortId;
    const footerText = `Protected - ${mainText}`;

    for (const page of pages) {
      const { width, height } = page.getSize();

      page.drawRectangle({
        x: 0, y: 0,
        width, height,
        color: rgb(1, 1, 1),
        opacity: 0,
      });

      const textWidth = mainText.length * 18;
      page.drawText(mainText, {
        x: width / 2 - textWidth / 2,
        y: height / 2,
        size: 40,
        font,
        color: rgb(0.8, 0.2, 0.2),
        opacity: 0.25,
        rotate: degrees(45),
      });

      page.drawText(mainText, {
        x: width / 2 - textWidth / 2,
        y: height / 2 + 120,
        size: 28,
        font,
        color: rgb(0.8, 0.2, 0.2),
        opacity: 0.18,
        rotate: degrees(45),
      });

      page.drawText(footerText, {
        x: 20, y: 10,
        size: 8, font,
        color: rgb(0.6, 0.1, 0.1),
        opacity: 0.6,
      });

      page.drawText(mainText, {
        x: 20, y: height - 20,
        size: 9, font,
        color: rgb(0.6, 0.1, 0.1),
        opacity: 0.6,
      });

      page.drawText(shortId, {
        x: width - (shortId.length * 7) - 10,
        y: height - 20,
        size: 9, font,
        color: rgb(0.6, 0.1, 0.1),
        opacity: 0.6,
      });
    }

    const watermarkedBytes = await pdfDoc.save();
    return new Blob([watermarkedBytes], { type: 'application/pdf' });
  } catch (err) {
    console.warn('[MyLibrary] Watermark failed:', err);
    return pdfBlob;
  }
}

// ─────────────────────────────────────────────────────
//  تحميل الملف مع Watermark
// ─────────────────────────────────────────────────────
async function downloadWithWatermark(fileUrl, fileName, userInfo) {
  try {
    const downloadUrl = getBackblazeDownloadUrl(fileUrl);
    if (!downloadUrl) return;

    const response = await fetch(downloadUrl);
    if (!response.ok) throw new Error('فشل تحميل الملف');

    const blob = await response.blob();
    const isPdf = blob.type === 'application/pdf' || (fileName || '').toLowerCase().endsWith('.pdf');

    let finalBlob = blob;
    if (isPdf) {
      // تحميل مؤقتاً لأن pdf-lib يتطلب Blob
      finalBlob = await addWatermarkToPdfBytes(blob, userInfo);
    }

    const objectUrl = URL.createObjectURL(finalBlob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName || 'download.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch (e) {
    console.error('Download failed:', e);
    return false;
  }
}

const TABS = [
  { id: 'pdf',  label: 'كتبي',   icon: FiBook },
  { id: 'zip',  label: 'ملفاتي', icon: FiFolder },
  { id: 'link', label: 'روابطي', icon: FiLink },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const months = ['يناير','فبراير','مارس','أبريل','مايو','يونيو',
                  'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};

const TypeBadge = ({ label, Icon }) => (
  <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-bold"
    style={{ background: '#f0f0f0', color: '#444', border: '1px solid #e0e0e0' }}>
    <Icon size={9} />{label}
  </div>
);

const DateBadge = ({ date }) => (
  <div className="px-2 py-0.5 rounded text-[9px] font-semibold"
    style={{ background: '#f5f5f5', color: '#888', border: '1px solid #e8e8e8' }}>
    {date}
  </div>
);

// ─────────────────────────────────────────────────────
//  BookCard مع مؤشر offline
// ─────────────────────────────────────────────────────
const BookCard = ({ item, index, navigate, user }) => {
  // ✅ تم تغيير item.service إلى item.services
  const service = item.services || {};
  const hasFile = !!service.file_url;
  const hasCover = !!service.image_url;
  // ✅ التوافق مع المنتجات القديمة: إذا لم يتم تعيين allow_download (null/undefined) نسمح بالتحميل افتراضياً
  const allowDownload = service.allow_download !== false;

  const [cached,        setCached]        = useState(false);
  const [caching,       setCaching]       = useState(false);
  const [cacheProgress, setCacheProgress] = useState(0);
  const [downloading,   setDownloading]   = useState(false);

  useEffect(() => {
    if (service.id) offlineStorage.isPDFCached(service.id).then(setCached);
  }, [service.id]);

  const handleCachePDF = async (e) => {
    e.stopPropagation();
    if (!service.file_url || caching || cached) return;
    setCaching(true); setCacheProgress(10);
    const interval = setInterval(() => setCacheProgress(p => Math.min(p + 15, 85)), 300);
    const ok = await offlineStorage.cachePDF(user?.id, service.id, getBackblazeDownloadUrl(service.file_url));
    clearInterval(interval);
    setCacheProgress(100);
    setTimeout(() => { setCaching(false); setCached(ok); setCacheProgress(0); }, 500);
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!service.file_url || downloading) return;
    setDownloading(true);
    const userInfo = { id: user?.id, email: user?.email || '' };
    const fileName = service.name ? `${service.name}.pdf` : 'download.pdf';
    const ok = await downloadWithWatermark(service.file_url, fileName, userInfo);
    setDownloading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, type: 'spring', stiffness: 280, damping: 24 }}
      onClick={() => hasFile && navigate(`/read?book=${service.id}`)}
      className="rounded-xl overflow-hidden flex flex-col cursor-pointer group"
      style={{
        background: '#fff', border: '1px solid #e8e8e8',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.2s, transform 0.2s',
        aspectRatio: '1 / 1.6', width: '100%',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.13)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div className="flex-[5] relative overflow-hidden bg-gray-50">
        {hasCover
          ? <img src={getMediaUrl(service.image_url)} alt={service.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><FiBook size={28} style={{ color: '#ccc' }} /></div>
        }
        <div className="absolute top-1.5 left-1.5"><DateBadge date={formatDate(item.purchased_at)} /></div>
        {cached && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow">
            <FiCheckCircle size={10} color="#fff" />
          </div>
        )}
        {caching && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
            <div className="h-full bg-gray-900 transition-all duration-300 rounded-full" style={{ width: `${cacheProgress}%` }} />
          </div>
        )}
        {hasFile && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all flex items-center justify-center">
            <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity">
              <FiPlay size={11} color="#111" style={{ marginLeft: 1 }} />
            </div>
          </div>
        )}
      </div>
      <div className="flex-[3] p-2 flex flex-col bg-white">
        <p className="text-gray-800 text-[10px] font-bold leading-tight line-clamp-2 mb-auto">{service.name || 'منتج رقمي'}</p>
        {hasFile && allowDownload && (
          <button onClick={handleDownload} disabled={downloading}
            className="mt-1 mb-1 h-5 rounded flex items-center justify-center gap-1 text-[8px] font-bold"
            style={{ background: '#111', color: '#fff' }}>
            {downloading ? <><FiClock size={8} /> جاري التحميل...</> : <><FiDownload size={8} /> تحميل</>}
          </button>
        )}
        {hasFile && !cached && navigator.onLine && (
          <button onClick={handleCachePDF} disabled={caching}
            className="mt-1 mb-1 h-5 rounded flex items-center justify-center gap-1 text-[8px] font-bold"
            style={{ background: '#f0f0f0', color: '#666', border: '1px solid #e0e0e0' }}>
            {caching ? <><FiClock size={8} /> {cacheProgress}%</> : <><FiHardDrive size={8} /> حفظ offline</>}
          </button>
        )}
        {cached && (
          <div className="mt-1 mb-1 h-5 rounded flex items-center justify-center gap-1 text-[8px] font-bold"
            style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>
            <FiCheckCircle size={8} /> متاح offline
          </div>
        )}
        <div className="mt-1 h-6 rounded-lg flex items-center justify-center"
          style={{ background: hasFile ? '#111' : '#f5f5f5' }}>
          <span className="text-[9px] font-bold" style={{ color: hasFile ? '#fff' : '#bbb' }}>
            {hasFile ? 'قراءة' : 'غير متاح'}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const ZipCard = ({ item, index, user }) => {
  // ✅ تم تغيير item.service إلى item.services
  const s = item.services || {};
  const hasFile = !!s.file_url;
  // ✅ التوافق مع المنتجات القديمة: null/undefined يعني مسموح بالتحميل
  const allowDownload = s.allow_download !== false;
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!s.file_url || downloading) return;
    setDownloading(true);
    // ZIP files don't get watermark but still download via regular URL
    const fileName = s.name ? `${s.name}.zip` : 'download.zip';
    const ok = await downloadWithWatermark(s.file_url, fileName, { id: user?.id, email: user?.email || '' });
    setDownloading(false);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      className="rounded-xl border overflow-hidden mb-3"
      style={{ background: '#fff', borderColor: '#e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      {s.image_url && (
        <div className="relative h-28 overflow-hidden bg-gray-100">
          <img src={getMediaUrl(s.image_url)} alt={s.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.12)' }} />
          <div className="absolute top-2 left-2"><TypeBadge label="ZIP" Icon={FiFolder} /></div>
          <div className="absolute top-2 right-2"><DateBadge date={formatDate(item.purchased_at)} /></div>
        </div>
      )}
      <div className="p-3.5">
        {!s.image_url && <div className="flex items-center justify-between mb-2"><TypeBadge label="ZIP" Icon={FiFolder} /><DateBadge date={formatDate(item.purchased_at)} /></div>}
        <p className="text-gray-800 font-bold text-sm mb-1">{s.name || 'ملف ZIP'}</p>
        {s.description && <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed mb-2">{s.description}</p>}
        {/* ✅ تم تغيير item.provider إلى item.providers */}
        {item.providers?.full_name && <p className="text-gray-400 text-[10px] mb-3">{item.providers.full_name}</p>}
        <button onClick={allowDownload ? handleDownload : () => hasFile && window.open(getBackblazeDownloadUrl(s.file_url), '_blank')}
          disabled={!hasFile || downloading}
          className="w-full h-9 rounded-lg flex items-center justify-center gap-2 text-sm font-bold"
          style={{ background: hasFile ? '#111' : '#f5f5f5', color: hasFile ? '#fff' : '#bbb' }}>
          {downloading ? <FiClock size={13} /> : <FiDownload size={13} />} {downloading ? 'جاري التحميل...' : 'تنزيل'}
        </button>
      </div>
    </motion.div>
  );
};

const LinkCard = ({ item, index }) => {
  // ✅ تم تغيير item.service إلى item.services
  const s = item.services || {};
  const hasLink = !!s.file_url;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      className="rounded-xl border overflow-hidden mb-3"
      style={{ background: '#fff', borderColor: '#e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      {s.image_url && (
        <div className="relative h-24 overflow-hidden bg-gray-100">
          <img src={getMediaUrl(s.image_url)} alt={s.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.12)' }} />
          <div className="absolute top-2 left-2"><TypeBadge label="رابط" Icon={FiLink} /></div>
          <div className="absolute top-2 right-2"><DateBadge date={formatDate(item.purchased_at)} /></div>
        </div>
      )}
      <div className="p-3.5">
        {!s.image_url && <div className="flex items-center justify-between mb-2"><TypeBadge label="رابط" Icon={FiLink} /><DateBadge date={formatDate(item.purchased_at)} /></div>}
        <p className="text-gray-800 font-bold text-sm mb-1">{s.name || 'رابط رقمي'}</p>
        {s.description && <p className="text-gray-400 text-xs line-clamp-2 mb-2 leading-relaxed">{s.description}</p>}
        {hasLink && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg mb-3" style={{ background: '#f5f5f5', border: '1px solid #e8e8e8' }}>
            <FiLink size={10} style={{ color: '#999' }} />
            <span className="text-[9px] truncate flex-1 text-gray-400">{s.file_url}</span>
          </div>
        )}
        <button onClick={() => hasLink && window.open(getBackblazeDownloadUrl(s.file_url) || s.file_url, '_blank')} disabled={!hasLink}
          className="w-full h-9 rounded-lg flex items-center justify-center gap-2 text-sm font-bold"
          style={{ background: hasLink ? '#111' : '#f5f5f5', color: hasLink ? '#fff' : '#bbb' }}>
          <FiExternalLink size={13} /> فتح الرابط
        </button>
      </div>
    </motion.div>
  );
};

const EmptyState = ({ icon: Icon, label }) => {
  const msgs = { 'كتبي': 'ليس لديك كتب PDF بعد', 'ملفاتي': 'ليس لديك ملفات ZIP بعد', 'روابطي': 'ليس لديك روابط مشتراة بعد' };
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#f5f5f5', border: '1px solid #e8e8e8' }}>
        <Icon size={28} style={{ color: '#ccc' }} />
      </div>
      <p className="text-gray-700 font-bold text-sm mb-1.5">{msgs[label]}</p>
      <p className="text-gray-400 text-xs text-center">المنتجات التي تشتريها ستظهر هنا</p>
    </div>
  );
};

const Shimmer = () => (
  <div className="grid gap-3 p-5" style={{ gridTemplateColumns: 'repeat(auto-fill, 130px)' }}>
    {[...Array(9)].map((_, i) => (
      <div key={i} className="rounded-xl overflow-hidden animate-pulse border border-gray-100" style={{ aspectRatio: '1/1.6', background: '#fafafa' }}>
        <div className="h-[60%] bg-gray-100" />
        <div className="p-2 space-y-1.5">
          <div className="h-2 bg-gray-100 rounded-full w-full" />
          <div className="h-2 bg-gray-50 rounded-full w-2/3" />
          <div className="h-5 bg-gray-100 rounded-lg mt-2" />
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────
//  MAIN
// ─────────────────────────────────────────────────────
const MyLibraryScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab,    setActiveTab]    = useState('pdf');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [pdfs,         setPdfs]         = useState([]);
  const [zips,         setZips]         = useState([]);
  const [links,        setLinks]        = useState([]);
  const [isOnline,     setIsOnline]     = useState(navigator.onLine);
  const [storageInfo,  setStorageInfo]  = useState(null);
  const [cachingAll,   setCachingAll]   = useState(false);

  useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => { offlineStorage.getStorageInfo().then(setStorageInfo); }, []);

  // ✅ دالة التقسيم تدعم الآن الأسماء الجديدة (services, providers)
  const splitByType = (list) => {
    setPdfs(list.filter(p  => (p.services?.product_type || 'pdf') === 'pdf'));
    setZips(list.filter(p  => p.services?.product_type  === 'zip'));
    setLinks(list.filter(p => p.services?.product_type  === 'link'));
  };

  const load = useCallback(async () => {
    if (!user?.id) { setError('يجب تسجيل الدخول'); setLoading(false); return; }
    setLoading(true); setError(null);

    // ✅ استخدام الدالة الجاهزة من supabaseService بدلاً من الاستعلام اليدوي
    // هذه الدالة تدعم Offline Storage وتقوم بعمل Join بشكل صحيح
    try {
      const list = await supabaseService.getClientLibrary(user.id);
      splitByType(list);
    } catch (err) {
      console.error('Library load error:', err);
      setError('فشل تحميل المكتبة');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const cacheAllPDFs = async () => {
    if (!user?.id || !navigator.onLine || cachingAll) return;
    setCachingAll(true);
    for (const item of pdfs) {
      // ✅ استخدام services
      const s = item.services || {};
      if (s.file_url && s.id) await offlineStorage.cachePDF(user.id, s.id, getBackblazeDownloadUrl(s.file_url));
    }
    const info = await offlineStorage.getStorageInfo();
    setStorageInfo(info);
    setCachingAll(false);
  };

  const totalCount  = pdfs.length + zips.length + links.length;
  const currentTab  = TABS.find(t => t.id === activeTab);
  const currentList = { pdf: pdfs, zip: zips, link: links }[activeTab];

  return (
    <div className="min-h-screen flex flex-col" dir="rtl" style={{ background: '#fafafa', fontFamily: 'Tajawal, sans-serif' }}>

      {/* Offline banner */}
      {!isOnline && (
        <div className="flex items-center justify-center gap-2 py-2 text-xs font-bold"
          style={{ background: '#fef3c7', color: '#92400e', borderBottom: '1px solid #fde68a' }}>
          <FiWifiOff size={12} /> وضع offline — تعرض الكتب المحفوظة فقط
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 border-b px-6 py-4 bg-white sticky top-0 z-10" style={{ borderColor: '#ebebeb' }}>
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl flex items-center justify-center border transition-colors hover:bg-gray-50"
            style={{ background: '#fff', borderColor: '#e0e0e0' }}>
            <FiArrowRight className="text-gray-700 text-sm" />
          </button>
          <div className="flex-1">
            <h1 className="text-gray-900 text-lg font-black tracking-tight">مكتبتي</h1>
            <p className="text-gray-400 text-[10px]">
              {storageInfo ? `مساحة مستخدمة: ${storageInfo.usedMB} MB` : 'منتجاتك الرقمية المشتراة'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isOnline && pdfs.length > 0 && (
              <button onClick={cacheAllPDFs} disabled={cachingAll}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[10px] font-bold transition-colors hover:bg-gray-50"
                style={{ background: '#fff', borderColor: '#e0e0e0', color: '#555' }}>
                <FiHardDrive size={11} />
                {cachingAll ? 'جاري الحفظ...' : 'حفظ الكل offline'}
              </button>
            )}
            {totalCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border" style={{ background: '#f5f5f5', borderColor: '#e0e0e0' }}>
                <FiBook size={11} className="text-gray-400" />
                <span className="text-gray-700 text-xs font-bold">{totalCount}</span>
              </div>
            )}
            <button onClick={load} disabled={loading || !isOnline}
              className="w-8 h-8 rounded-xl flex items-center justify-center border transition-colors hover:bg-gray-50"
              style={{ background: '#fff', borderColor: '#e0e0e0', opacity: !isOnline ? 0.4 : 1 }}>
              <FiRefreshCw size={13} className={`text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex-shrink-0 border-b bg-white sticky top-[69px] z-10" style={{ borderColor: '#ebebeb' }}>
        <div className="max-w-5xl mx-auto flex">
          {TABS.map(tab => {
            const count  = { pdf: pdfs.length, zip: zips.length, link: links.length }[tab.id];
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center justify-center gap-1.5 px-5 py-3.5 relative transition-all text-sm"
                style={{ color: active ? '#111' : '#999' }}>
                {active && <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-gray-900" />}
                <tab.icon size={13} />
                <span className="font-bold text-xs">{tab.label}</span>
                {count > 0 && (
                  <span className="text-[8px] font-black px-1.5 py-0.5 rounded-full"
                    style={{ background: active ? '#111' : '#f0f0f0', color: active ? '#fff' : '#888' }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1">
        <div className="max-w-5xl mx-auto">
          {loading ? <Shimmer />
          : error ? (
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <FiWifiOff size={40} className="text-gray-300 mb-4" />
              <p className="text-gray-400 text-sm text-center">{error}</p>
              {isOnline && (
                <button onClick={load}
                  className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl border text-gray-600 text-sm font-semibold hover:bg-gray-50"
                  style={{ background: '#fff', borderColor: '#e0e0e0' }}>
                  <FiRefreshCw size={13} /> إعادة المحاولة
                </button>
              )}
            </div>
          ) : currentList.length === 0 ? (
            <EmptyState icon={currentTab.icon} label={currentTab.label} />
          ) : activeTab === 'pdf' ? (
            <div className="grid gap-3 p-5" style={{ gridTemplateColumns: 'repeat(auto-fill, 130px)' }}>
              {pdfs.map((item, i) => (
                <BookCard key={item.id || i} item={item} index={i} navigate={navigate} user={user} />
              ))}
            </div>
          ) : (
            <div className="p-5 max-w-2xl">
              {currentList.map((item, i) =>
                activeTab === 'zip'
                  ? <ZipCard  key={item.id || i} item={item} index={i} user={user} />
                  : <LinkCard key={item.id || i} item={item} index={i} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyLibraryScreen;